import { useMemo } from 'react';
import { z } from 'zod';
import { RudoPublishDtoRestrictionEnum } from '../../social-export.types';

const RUDO_TITLE_MAX_LENGTH = 200;

/** Parses Autocomplete matchId folder value into a required folder id. */
function folderIdSchema() {
  return z
    .number({
      required_error: 'Required',
      invalid_type_error: 'Required',
    })
    .nullable()
    .refine((n): n is number => n != null && !Number.isNaN(n), { message: 'Required' });
}

/** Parses restriction select value ("0"|"1"|"2" or number) into the API enum. */
function restrictionSchema() {
  return z.union([z.literal('0'), z.literal('1'), z.literal('2'), z.number()]).transform((val) => {
    const n = typeof val === 'number' ? val : Number(val);
    if (n === 1) return RudoPublishDtoRestrictionEnum['1'];
    if (n === 2) return RudoPublishDtoRestrictionEnum['2'];
    return RudoPublishDtoRestrictionEnum['0'];
  });
}

/** Builds the Zod schema for the Rudo publish form. */
export function createRudoPublishSchema() {
  return z.object({
    title: z.string().trim().min(1).max(RUDO_TITLE_MAX_LENGTH),
    content: z.string().trim().min(1),
    tags: z.string().optional(),
    folderId: folderIdSchema(),
    restriction: restrictionSchema(),
  });
}

/** Form input values before Zod transforms. */
export type RudoPublishFormValues = z.input<ReturnType<typeof createRudoPublishSchema>>;
/** Parsed publish values after Zod transforms. */
export type RudoPublishParsedValues = z.output<ReturnType<typeof createRudoPublishSchema>>;

/** Zod schema for the Rudo publish form. */
export function useRudoPublishSchema() {
  return useMemo(() => createRudoPublishSchema(), []);
}
